import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

import {
  Container,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Grid,
  GridItem,
  useDisclosure,
} from '@chakra-ui/react';

import Header from './Header';
import Sidebar from './Sidebar';

interface LayoutProps {
  children: React.ReactNode;
  backPath?: string;
}

const Layout = ({ children, backPath }: LayoutProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const sidebarWidth = { base: 'full', md: '260px' };
  const location = useLocation();
  const showSidebar =
    location.pathname === '/home' || location.pathname === '/top';
  const prevPathRef = useRef(location.pathname);

  // Close drawer only when navigating to a new path
  useEffect(() => {
    if (isOpen && prevPathRef.current !== location.pathname) {
      onClose();
    }
    // Update the previous path ref
    prevPathRef.current = location.pathname;
  }, [location.pathname, onClose, isOpen]);

  return (
    <Grid
      templateRows="auto 1fr"
      sx={{
        height: ['100dvh', '100vh'],
      }}
      minW={'100%'}
    >
      <GridItem position="sticky" top="0" zIndex="1100">
        <Header backPath={backPath} onOpen={showSidebar ? onOpen : undefined} />
      </GridItem>

      {/* Main Content Area */}
      <GridItem display="flex" width={'100%'} minHeight="0">
        <Grid
          templateColumns={
            showSidebar ? { base: '1fr', md: `${sidebarWidth.md} 1fr` } : '1fr'
          }
          height="100%"
          width={'100%'}
        >
          {/* Desktop Sidebar */}
          {showSidebar && (
            <GridItem
              as="nav"
              bg="white"
              borderRight="1px"
              borderRightColor="gray.200"
              pos="fixed"
              h="100%"
              display={{ base: 'none', md: 'block' }}
              w={sidebarWidth}
              top="0"
              zIndex="1100"
            >
              <Sidebar onClose={onClose} />
            </GridItem>
          )}

          {/* Mobile Drawer */}
          {showSidebar && (
            <Drawer
              isOpen={isOpen}
              placement="left"
              onClose={onClose}
              returnFocusOnClose={false}
              onOverlayClick={onClose}
              size="xs"
              closeOnEsc={true}
            >
              <DrawerOverlay
                bg="blackAlpha.600"
                backdropFilter="blur(4px)"
                sx={{
                  // User's motion preferences
                  '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                  },
                }}
              />
              <DrawerContent
                maxW="320px"
                w="320px"
                bg="white"
                boxShadow="md"
                sx={{
                  // Hardware acceleration for smooth performance
                  transform: 'translateZ(0)',
                  backfaceVisibility: 'hidden',
                  '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                  },
                }}
              >
                <Sidebar onClose={onClose} isInDrawer={true} />
              </DrawerContent>
            </Drawer>
          )}

          {/* Content */}
          <GridItem
            colStart={showSidebar ? { base: 1, md: 2 } : 1}
            as="main"
            display="flex"
            flexDirection="column"
            width={'100%'}
            transition=".3s ease"
            flex={1}
            height="100%"
            overflow="auto"
          >
            <Container
              maxW="1300px"
              width="100%"
              px={{ base: 4, md: 6, '2xl': 6 }}
              py={{ base: 3, md: 4, '2xl': 6 }}
              pb={{ base: 6 }}
              display="flex"
              flexDirection="column"
              flex={1}
            >
              {children}
            </Container>
          </GridItem>
        </Grid>
      </GridItem>
    </Grid>
  );
};

export default Layout;
